import { useEffect, useLayoutEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { useRouter } from 'next/router';
import 'react-toastify/dist/ReactToastify.min.css';
import Header from '~/components/common/header';
import Footer from '~/components/common/footer';
import Quickview from '~/components/features/product/common/quickview-modal';
import { showScrollTopHandler, stickyHeaderHandler, stickyFooterHandler } from '~/utils';
import { useProgram, useAuth } from 'hooks';
import { useGeneral } from 'hooks/useGeneral';
import { Modal } from '@mui/material';
import MyBirthDateForm from '~/components/partials/modals/dateOfBirthModal';
import Trivias from '~/components/partials/trivias';
import { subDays } from 'date-fns';

function LayoutDesktop({ children }) {
  const { program } = useProgram();
  const { isLoggedIn, isInitialised, participant } = useAuth();
  const { closeModal } = useGeneral();
  const router = useRouter();
  const [open, setOpen] = useState(true);

  useLayoutEffect(() => {
    document.querySelector('body').classList.remove('loaded');
  }, [router.pathname]);

  useEffect(() => {
    window.addEventListener('scroll', showScrollTopHandler, true);
    window.addEventListener('scroll', stickyHeaderHandler, true);
    window.addEventListener('scroll', stickyFooterHandler, true);
    window.addEventListener('resize', stickyHeaderHandler, true);
    window.addEventListener('resize', stickyFooterHandler, true);

    return () => {
      window.removeEventListener('scroll', showScrollTopHandler, true);
      window.removeEventListener('scroll', stickyHeaderHandler, true);
      window.removeEventListener('scroll', stickyFooterHandler, true);
      window.removeEventListener('resize', stickyHeaderHandler, true);
      window.removeEventListener('resize', stickyFooterHandler, true);
    };
  }, []);

  useEffect(() => {
    closeModal();

    const body = document.querySelector('body');
    if (body) {
      body.className = ''; // Limpiar clases
      setTimeout(() => {
        body.classList.add('loaded');
      }, 50);
    }
  }, [router.pathname]);

  useEffect(() => {
    // Esperar a que hydrateSession termine antes de tomar decisiones sobre
    // participant. Si isInitialised es false, participant puede ser {} y
    // dispararíamos el redirect a privacy-policy sin motivo real.
    if (!isInitialised) return;
    if (
      isLoggedIn &&
      !participant.approvedPolicy &&
      !participant.approvedTermsAndConditions &&
      router.pathname !== '/pages/privacy-policy'
    ) {
      router.push('/pages/privacy-policy');
    }
  }, [isInitialised, isLoggedIn, participant, router.pathname]);

  useEffect(() => {
    if (
      program?.id === 26 &&
      isLoggedIn &&
      participant?.passwordUpdatedAt &&
      participant.approvedPolicy &&
      participant.approvedTermsAndConditions
    ) {
      const passwordUpdatedDate = new Date(participant.passwordUpdatedAt);
      const comparisonDate = subDays(new Date(), 365);

      if (passwordUpdatedDate <= comparisonDate) {
        router.push('/pages/change-my-password');
      }
    }
  }, [
    isLoggedIn,
    participant?.passwordUpdatedAt,
    participant?.approvedPolicy,
    participant?.approvedTermsAndConditions,
    program?.id,
  ]);

  useEffect(() => {
    // Race fix: si `program` todavía no cargó, `!program?.isStoreActive` es
    // true por vacuidad y mandaríamos al usuario a home aunque su tienda esté
    // activa. Esperar a tener el programa cargado antes de decidir. Se
    // manifiesta al volver al tab después de mucho tiempo cuando la pestaña
    // se re-monta y AuthProvider hidrata antes que ProgramProvider.
    if (!isInitialised || !program) return;
    if (isLoggedIn && !program.isStoreActive) {
      router.push('/');
    }
  }, [isInitialised, isLoggedIn, program]);

  useEffect(() => {
    const programsRequirePasswordUpdate = [38, 3, 2, 7, 6];
    if (
      isLoggedIn &&
      participant?.passwordUpdatedAt &&
      participant?.createdAt &&
      program?.id &&
      programsRequirePasswordUpdate.includes(program.id)
    ) {
      const passwordUpdatedDate = new Date(participant.passwordUpdatedAt);
      const createdDate = new Date(participant.createdAt);

      // Fix backend bug: passwordUpdatedAt is 1 year behind, so add 1 year
      passwordUpdatedDate.setFullYear(passwordUpdatedDate.getFullYear() + 1);

      // Check if password has never been changed (dates are the same or within 5 seconds)
      const timeDifferenceInSeconds =
        Math.abs(passwordUpdatedDate.getTime() - createdDate.getTime()) / 1000;

      if (timeDifferenceInSeconds < 5) {
        // Check if user has been prompted in the last 30 minutes
        const promptData = sessionStorage.getItem('passwordChangePrompted');
        const now = Date.now();
        let shouldPrompt = true;

        if (promptData) {
          try {
            const timestamp = parseInt(promptData, 10);
            const thirtyMinutesInMs = 30 * 60 * 1000;
            if (now - timestamp < thirtyMinutesInMs) {
              shouldPrompt = false;
            }
          } catch (e) {
            // Invalid data, will prompt again
          }
        }

        if (shouldPrompt && router.pathname !== '/pages/change-my-password') {
          sessionStorage.setItem('passwordChangePrompted', now.toString());
          router.push('/pages/change-my-password');
        }
      }
    }
  }, [
    isLoggedIn,
    participant?.passwordUpdatedAt,
    participant?.createdAt,
    program?.id,
    router.pathname,
  ]);

  if (!program) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'fixed',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      ></div>
    );
  }

  return (
    <div>
      <div className="page-wrapper" style={{ position: 'relative' }}>
        {isLoggedIn && <Header />}
        {children}
        {isLoggedIn && <Footer />}
      </div>

      <ToastContainer
        autoClose={3000}
        // @ts-ignore
        duration={300}
        newestOnTop={true}
        className="toast-container"
        position="bottom-left"
        closeButton={true}
        hideProgressBar={true}
        limit={1}
      />

      <Quickview />

      {isLoggedIn && <Trivias />}
      {isLoggedIn &&
        program &&
        (program.id === 54 || program.id === 33 || program.id === 56) &&
        participant.dateOfBirth === null && (
          <MyBirthDateForm open={open} onClose={() => setOpen(false)} />
        )}
    </div>
  );
}

export default LayoutDesktop;
